import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { BulkItemAction, Category, ItemStatus, Orientation, WhereKind } from "@instead/shared";
import { api } from "../lib/api.ts";
import { moodLabel, useT } from "../lib/i18n.ts";
import { catLabel } from "./DealCard.tsx";
import { CategoryRows, EditLink, PlaceRows } from "./ItemForm.tsx";
import { Ic } from "./Icons.tsx";
import { NeedChips } from "./NeedChips.tsx";
import { ChipGroup, Sheet } from "./Ui.tsx";

/**
 * What a bulk edit will change (D-37). Every field is three-state: left out
 * (undefined) = untouched on every item; a value = set on every item; null or
 * an empty list = cleared. Applying never blanks a field nobody touched.
 */
export interface BulkEdit {
  categoryId?: string | null;
  moods?: string[];
  orientation?: Orientation | null;
  needIds?: string[];
  where?: { whereKind: WhereKind; placeId: string | null };
  pinned?: boolean;
  status?: ItemStatus;
}

export type Detour = "/settings/categories" | "/settings/places" | "/settings/moods" | "/settings/needs";

/** The API calls a sheet state amounts to, in the order they are applied. */
export function bulkActions(e: BulkEdit): BulkItemAction["action"][] {
  const out: BulkItemAction["action"][] = [];
  if (e.categoryId !== undefined) out.push({ kind: "setCategory", categoryId: e.categoryId });
  if (e.moods !== undefined) out.push({ kind: "setMoods", moods: e.moods });
  if (e.orientation !== undefined) out.push({ kind: "setOrientation", orientation: e.orientation });
  if (e.needIds !== undefined) out.push({ kind: "setNeeds", needIds: e.needIds });
  if (e.where !== undefined) out.push({ kind: "setWhere", whereKind: e.where.whereKind, placeId: e.where.placeId });
  if (e.pinned !== undefined) out.push({ kind: "setPinned", pinned: e.pinned });
  if (e.status !== undefined) out.push({ kind: "setStatus", status: e.status });
  return out;
}

/**
 * The shelf's Edit… sheet (feedback F9b): every facet the item form has, for a
 * whole selection at once. Each picker keeps its pencil to the editor where
 * that vocabulary lives; the selection and these choices wait in a device
 * draft while you are away.
 */
export function BulkEditSheet({ n, edit, cats, pending, error, onChange, onApply, onClose, onDetour }: {
  n: number;
  edit: BulkEdit;
  cats: Category[];
  pending: boolean;
  error: string | null;
  onChange: (next: BulkEdit) => void;
  onApply: () => void;
  onClose: () => void;
  onDetour: (to: Detour) => void;
}) {
  const t = useT();
  const [sub, setSub] = useState<"category" | "where" | null>(null);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const { data: moodsData } = useQuery({ queryKey: ["moods"], queryFn: api.moods.list });
  const { data: placesData } = useQuery({ queryKey: ["places"], queryFn: api.places.list, enabled: settings?.features.context ?? false });
  const places = placesData?.places ?? [];
  const whereOn = settings?.features.context ?? false;
  const needsOn = settings?.features.needs ?? false;
  const set = (patch: Partial<BulkEdit>) => onChange({ ...edit, ...patch });
  const keep = (field: keyof BulkEdit) => {
    const next = { ...edit };
    delete next[field];
    onChange(next);
  };
  const nothing = bulkActions(edit).length === 0;

  const cat = edit.categoryId ? cats.find((c) => c.id === edit.categoryId) : undefined;
  const catText = edit.categoryId === undefined ? t("add.choose")
    : edit.categoryId === null ? t("shelf.unsorted")
    : cat ? (cat.parentId ? `${catLabel(cats.find((c) => c.id === cat.parentId), t)} › ${cat.name}` : catLabel(cat, t)) : t("add.choose");
  const place = edit.where?.whereKind === "place" ? places.find((p) => p.id === edit.where?.placeId) : undefined;

  return (
    <>
      <Sheet title={t("shelf.editN", { n })} onClose={onClose}
        action={<button type="button" className="btn btn-primary" style={{ minHeight: 36 }} disabled={nothing || pending} onClick={onApply}>{t("shelf.apply")}</button>}>
        <p className="meta" style={{ marginTop: 0 }}>{t("shelf.bulkHint")}</p>

        <div className="section">
          <div className="eyebrow">{t("add.category")}<EditLink label={t("add.editCategories")} onClick={() => onDetour("/settings/categories")} /></div>
          <div className="chip-row" role="group" aria-label={t("add.category")}>
            <Leave on={edit.categoryId === undefined} onClick={() => keep("categoryId")} />
            <button type="button" className="chip" aria-pressed={edit.categoryId !== undefined} onClick={() => setSub("category")}>{catText} {Ic.down({ size: 12 })}</button>
          </div>
        </div>

        {(moodsData?.moods.length ?? 0) > 0 && (
          <div className="section">
            <div className="eyebrow">{t("rest.feel")}<EditLink label={t("add.editMoods")} onClick={() => onDetour("/settings/moods")} /></div>
            <div className="chip-row" style={{ marginBottom: "var(--sp-2)" }}>
              <Leave on={edit.moods === undefined} onClick={() => keep("moods")} />
              <button type="button" className="chip" aria-pressed={edit.moods !== undefined && edit.moods.length === 0} onClick={() => set({ moods: [] })}>{t("shelf.clear")}</button>
            </div>
            <ChipGroup multi label={t("rest.feel")} selected={edit.moods ?? []} options={(moodsData?.moods ?? []).map((m) => ({ value: m.key, label: moodLabel(m) }))} onChange={(moods) => set({ moods })} />
          </div>
        )}

        <div className="section">
          <div className="eyebrow">{t("add.about")}</div>
          <div className="chip-row" style={{ marginBottom: "var(--sp-2)" }}>
            <Leave on={edit.orientation === undefined} onClick={() => keep("orientation")} />
            <button type="button" className="chip" aria-pressed={edit.orientation === null} onClick={() => set({ orientation: null })}>{t("shelf.clear")}</button>
          </div>
          <ChipGroup label={t("add.about")} selected={edit.orientation ? [edit.orientation] : []}
            options={(["create", "consume", "be"] as const).map((o) => ({ value: o, label: t(`orient.${o}`) }))}
            onChange={(v) => set({ orientation: (v[0] as Orientation | undefined) ?? null })} />
        </div>

        {whereOn && (
          <div className="section">
            <div className="eyebrow">{t("add.where")}<EditLink label={t("add.editPlaces")} onClick={() => onDetour("/settings/places")} /></div>
            <div className="chip-row" role="group" aria-label={t("add.where")}>
              <Leave on={edit.where === undefined} onClick={() => keep("where")} />
              {(["anywhere", "indoor", "outdoor"] as const).map((k) => (
                <button key={k} type="button" className="chip" aria-pressed={edit.where?.whereKind === k} onClick={() => set({ where: { whereKind: k, placeId: null } })}>{t(`where.${k}`)}</button>
              ))}
              <button type="button" className="chip" aria-pressed={edit.where?.whereKind === "place"} onClick={() => setSub("where")}>
                {place ? place.name : t("where.place")}
              </button>
            </div>
          </div>
        )}

        {needsOn && (
          <div className="section">
            <div className="eyebrow">{t("add.needs")}<EditLink label={t("add.editNeeds")} onClick={() => onDetour("/settings/needs")} /></div>
            <div className="chip-row" style={{ marginBottom: "var(--sp-2)" }}>
              <Leave on={edit.needIds === undefined} onClick={() => keep("needIds")} />
              <button type="button" className="chip" aria-pressed={edit.needIds !== undefined && edit.needIds.length === 0} onClick={() => set({ needIds: [] })}>{t("shelf.clear")}</button>
            </div>
            <NeedChips label={t("add.needs")} selected={edit.needIds ?? []} onChange={(needIds) => set({ needIds })} />
          </div>
        )}

        <div className="section">
          <div className="eyebrow">{t("shelf.pinLabel")}</div>
          <div className="chip-row" role="group" aria-label={t("shelf.pinLabel")}>
            <Leave on={edit.pinned === undefined} onClick={() => keep("pinned")} />
            <button type="button" className="chip" aria-pressed={edit.pinned === true} onClick={() => set({ pinned: true })}>{Ic.pin({ size: 12 })} {t("shelf.pin")}</button>
            <button type="button" className="chip" aria-pressed={edit.pinned === false} onClick={() => set({ pinned: false })}>{t("shelf.unpin")}</button>
          </div>
        </div>

        <div className="section">
          <div className="eyebrow">{t("shelf.progress")}</div>
          <div className="chip-row" role="group" aria-label={t("shelf.progress")}>
            <Leave on={edit.status === undefined} onClick={() => keep("status")} />
            {(["unstarted", "in_progress", "done"] as const).map((s) => (
              <button key={s} type="button" className="chip" aria-pressed={edit.status === s} onClick={() => set({ status: s })}>{t(`item.status.${s}`)}</button>
            ))}
          </div>
        </div>

        {error && <p role="alert" className="meta" style={{ color: "var(--danger)", marginTop: "var(--sp-3)" }}>{error}</p>}

        <button type="button" className="btn btn-primary btn-block" style={{ marginTop: "var(--sp-5)" }} disabled={nothing || pending} onClick={onApply}>{t("shelf.apply")}</button>
      </Sheet>

      {sub === "category" && (
        <Sheet title={t("add.category")} onClose={() => setSub(null)}
          action={<button type="button" className="icon-btn accent" onClick={() => onDetour("/settings/categories")}>{Ic.edit({ size: 14 })} {t("add.editCategories")}</button>}>
          <button type="button" className="sheet-row" onClick={() => { set({ categoryId: null }); setSub(null); }}><span className="grow">{t("shelf.unsorted")}</span>{edit.categoryId === null && Ic.check({ size: 16 })}</button>
          {cats.filter((c) => !c.parentId).map((c) => (
            <CategoryRows key={c.id} c={c} cats={cats} selected={edit.categoryId ?? ""} onPick={(id) => { set({ categoryId: id }); setSub(null); }} />
          ))}
        </Sheet>
      )}

      {sub === "where" && (
        <Sheet title={t("add.where")} onClose={() => setSub(null)}
          action={<button type="button" className="icon-btn accent" onClick={() => onDetour("/settings/places")}>{Ic.edit({ size: 14 })} {t("add.editPlaces")}</button>}>
          <PlaceRows places={places} selected={edit.where?.placeId ?? null} onPick={(id) => { set({ where: { whereKind: "place", placeId: id } }); setSub(null); }} />
        </Sheet>
      )}
    </>
  );
}

/** The default of every field: this one is not touched. */
function Leave({ on, onClick }: { on: boolean; onClick: () => void }) {
  const t = useT();
  return <button type="button" className="chip" aria-pressed={on} onClick={onClick}>{t("shelf.leave")}</button>;
}
